import {
  find,
  propEq,
  path,
  prop,
  pipe,
  filter,
  ifElse,
  map,
  any,
  append,
  reduce,
  converge,
  concat,
  groupBy,
  pathOr,
  propOr,
  juxt,
  sum,
  multiply,
  add,
  both,
  subtract,
  max,
  chain,
  always,
  has,
  over,
  lensPath,
  assoc,
  identity,
} from 'ramda'
import { cReduce, sumProp, sumWith } from '@v/util/briqRamdaExt.js'
import { DiscountType } from './BookingDetailsApiModel.js'

// rounding to 5 decimals to handle floating point error
const round = n => Math.round(n * 100000) / 100000

export const PriceField = Object.freeze({
  IncludingTax: 'IncludingTax',
  ExcludingTax: 'ExcludingTax',
  Tax: 'Tax',
})

const mergeDealAndProductCosts = converge(concat, [
  propOr([], 'ProductGroups'),
  propOr([], 'BookingProducts'),
])

const getBookingProductsFromDeals = pipe(
  propOr([], 'ProductGroups'),
  chain(prop('BookingProducts')),
)

const flattenBookingProductCosts = converge(concat, [
  getBookingProductsFromDeals,
  propOr([], 'BookingProducts'),
])

export const getSubtotal = thing =>
  converge((costs, fee) => reduce((a, n) => a + n.TotalPrice[thing], fee, costs), [
    mergeDealAndProductCosts,
    pathOr(0, ['BookingFee', thing]),
  ])

const getSubtotalWithoutFee = thing =>
  pipe(
    mergeDealAndProductCosts,
    reduce((a, n) => a + n.TotalPrice[thing], 0),
  )

export const getSubtotalIncludingTax = getSubtotal(PriceField.IncludingTax)

export const getSubtotalExcludingTax = getSubtotal(PriceField.ExcludingTax)

export const getSubtotalTax = bookingCosts => getSubtotal(PriceField.Tax)(bookingCosts)

const sumVouchersExcludingTax = pipe(
  prop('Vouchers'),
  filter(propEq('IncludesTax', false)),
  sumProp('CalculatedAmount'),
)

const sumVouchersIncludingTax = pipe(
  prop('Vouchers'),
  filter(prop('IncludesTax')),
  sumProp('CalculatedAmount'),
)

const sumAbsoluteDiscountsExcludingTax = pipe(
  prop('Discounts'),
  filter(both(propEq('IncludesTax', false), propEq('Type', 'Price'))),
  sumProp('Value'),
)

const sumRelativeDiscountsExcludingTax = bookingCosts =>
  pipe(
    prop('Discounts'),
    filter(both(propEq('IncludesTax', false), propEq('Type', 'Percent'))),
    sumWith(x => (x.Value / 100) * getSubtotalWithoutFee(PriceField.ExcludingTax)(bookingCosts)),
  )(bookingCosts)

const sumDiscountsExcludingTax = converge(add, [
  sumAbsoluteDiscountsExcludingTax,
  sumRelativeDiscountsExcludingTax,
])

const sumAbsoluteDiscountsIncludingTax = pipe(
  prop('Discounts'),
  filter(both(propEq('IncludesTax', true), propEq('Type', 'Price'))),
  sumProp('Value'),
)

const sumRelativeDiscountsIncludingTax = bookingCosts =>
  pipe(
    prop('Discounts'),
    filter(both(propEq('IncludesTax', true), propEq('Type', 'Percent'))),
    sumWith(x => (x.Value / 100) * getSubtotalWithoutFee(PriceField.IncludingTax)(bookingCosts)),
  )(bookingCosts)

const sumDiscountsIncludingTax = converge(add, [
  sumAbsoluteDiscountsIncludingTax,
  sumRelativeDiscountsIncludingTax,
])

export const getAverageTaxRate = bookingCosts => {
  const T = getSubtotalTax(bookingCosts)
  if (T === 0) return 0
  return T / getSubtotalWithoutFee(PriceField.ExcludingTax)(bookingCosts)
}

const sumDiscountsAndVouchersExlcudingTax = bookingCosts => {
  return sumDiscountsExcludingTax(bookingCosts) + sumVouchersExcludingTax(bookingCosts)
}

const sumDiscountsAndVouchersInlcudingTax = bookingCosts => {
  return sumDiscountsIncludingTax(bookingCosts) + sumVouchersIncludingTax(bookingCosts)
}

export const totalDiscountsAndVouchersBeforeTax = bookingCosts => {
  const A = sumDiscountsAndVouchersInlcudingTax(bookingCosts)
  const B = sumDiscountsAndVouchersExlcudingTax(bookingCosts)
  const removeTax = cost => {
    if (cost === 0) return 0
    const t = getAverageTaxRate(bookingCosts)
    return cost / (1 + t)
  }
  return removeTax(A) + B
}

export const totalDiscountsAndVouchersAfterTax = bookingCosts => {
  const A = sumDiscountsAndVouchersInlcudingTax(bookingCosts)
  const B = sumDiscountsAndVouchersExlcudingTax(bookingCosts)
  const t = getAverageTaxRate(bookingCosts)
  const rawResult = A + (1 + t) * B
  return round(rawResult)
}

export const getTotalExcludingTax = pipe(
  converge(subtract, [getSubtotalExcludingTax, totalDiscountsAndVouchersBeforeTax]),
  max(0),
)

export const getTotalIncludingTax = pipe(
  converge(subtract, [getSubtotalIncludingTax, totalDiscountsAndVouchersAfterTax]),
  max(0),
)

const getProductPrices = pipe(flattenBookingProductCosts, map(prop('TotalPrice')))

const groupTaxes = pipe(
  groupBy(prop('TaxName')),
  map(reduce((a, n) => ({ Tax: a.Tax + n.Tax, TaxRate: n.TaxRate }), { Tax: 0 })),
)

export const getTaxesByNameWithoutDiscountsAndWithouFee = pipe(getProductPrices, groupTaxes)

const applyDiscountsToTax = (D, S, T) => {
  if (D === 0) return T
  const f = D < S ? 1 - D / S : 0
  return map(Ti => ({ ...Ti, Tax: round(f * Ti.Tax) }), T)
}

const getTaxesByNameWithoutFee = converge(applyDiscountsToTax, [
  totalDiscountsAndVouchersBeforeTax,
  getSubtotalWithoutFee(PriceField.ExcludingTax),
  getTaxesByNameWithoutDiscountsAndWithouFee,
])

const handleFee = BookingFee =>
  ifElse(
    has(BookingFee?.TaxName),
    over(lensPath([BookingFee?.TaxName, 'Tax']), add(BookingFee?.Tax)),
    assoc(BookingFee?.TaxName, { Tax: BookingFee?.Tax, TaxRate: BookingFee?.TaxRate }),
  )

export const getTaxesByName = chain(
  fee => pipe(getTaxesByNameWithoutFee, ifElse(always(fee), handleFee(fee), identity)),
  prop('BookingFee'),
)
